const GS = '\u001D';
const FIXED_DATE_AIS = new Set(['11', '13', '15', '16', '17']);
const SUPPORTED_AIS = new Set(['01', '10', '11', '13', '15', '16', '17', '21']);

function cleanRaw(value) {
  return String(value ?? '').replace(/^[\r\n\t]+|[\r\n\t]+$/g, '').trim();
}

function normalizePlain(value) {
  return cleanRaw(value).replace(/\s+/g, '');
}

export function normalizeGtin(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (![8, 12, 13, 14].includes(digits.length)) return '';
  return digits.padStart(14, '0');
}

function parseGs1Date(value) {
  const match = /^(\d{2})(\d{2})(\d{2})$/.exec(String(value || ''));
  if (!match) return '';
  const year = 2000 + Number(match[1]);
  const month = Number(match[2]);
  let day = Number(match[3]);
  if (month < 1 || month > 12) return '';
  if (day === 0) day = new Date(year, month, 0).getDate();
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return '';
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function recoverEmbeddedTraceFields(fields) {
  const next = { ...(fields || {}) };
  if (next['17']) return next;

  for (const sourceAi of ['10', '21']) {
    const value = String(next[sourceAi] || '').trim();
    if (!value) continue;

    const candidates = [...value.matchAll(/17(\d{6})10/g)];
    const match = [...candidates].reverse().find(candidate => parseGs1Date(candidate[1]));
    if (!match || match.index === undefined) continue;

    const prefix = value.slice(0, match.index);
    const suffix = value.slice(match.index + match[0].length);
    if (!prefix || !suffix) continue;

    // Some healthcare DataMatrix scanners omit the FNC1 separator after a
    // variable-length serial field. The result can arrive as one AI 10/21
    // value even though the payload visibly contains AI 17 + AI 10 later.
    // Recover those trace fields so the form prefills the physical label data.
    next['17'] = match[1];
    next['10'] = suffix;
    if (!next['21'] || sourceAi === '21') next['21'] = prefix;
    break;
  }

  return next;
}

function fieldsToResult(fields) {
  const recovered = recoverEmbeddedTraceFields(fields);
  const gtin = normalizeGtin(recovered['01']);
  const expirationDate = parseGs1Date(recovered['17']);
  const lot = String(recovered['10'] || '').trim();
  const serial = String(recovered['21'] || '').trim();
  if (!gtin && !expirationDate && !lot && !serial) return null;
  return { gtin, expirationDate, lot, serial };
}

function parseParenthesized(value) {
  const text = cleanRaw(value);
  const matches = [...text.matchAll(/\((01|10|11|13|15|16|17|21)\)/g)];
  if (!matches.length) return null;
  const fields = {};
  matches.forEach((match, index) => {
    const start = match.index + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index : text.length;
    fields[match[1]] = text.slice(start, end).trim();
  });
  return fieldsToResult(fields);
}

function parseDigitalLink(value) {
  const text = cleanRaw(value);
  if (!/^https?:\/\//i.test(text)) return null;
  try {
    const url = new URL(text);
    const parts = url.pathname.split('/').filter(Boolean).map(part => decodeURIComponent(part));
    const fields = {};
    for (let i = 0; i < parts.length - 1; i += 1) {
      if (SUPPORTED_AIS.has(parts[i])) fields[parts[i]] = parts[i + 1];
    }
    SUPPORTED_AIS.forEach(ai => {
      if (!fields[ai] && url.searchParams.has(ai)) fields[ai] = url.searchParams.get(ai);
    });
    return fieldsToResult(fields);
  } catch (_) {
    return null;
  }
}

function stripAimPrefix(value) {
  return String(value || '').replace(/^\](?:d2|D2|Q3|q3|C1|c1)/, '');
}

function parseRawGs1(value) {
  const text = stripAimPrefix(cleanRaw(value));
  if (!text) return null;
  const looksGs1 = text.includes(GS) || /^(?:01\d{14})(?:(?:10|11|13|15|16|17|21)|$)/.test(text);
  if (!looksGs1) return null;

  const fields = {};
  let index = 0;
  let recognized = 0;
  while (index < text.length) {
    if (text[index] === GS) {
      index += 1;
      continue;
    }

    const ai = text.slice(index, index + 2);
    if (!SUPPORTED_AIS.has(ai)) break;
    index += 2;
    recognized += 1;

    if (ai === '01') {
      fields[ai] = text.slice(index, index + 14);
      index += 14;
      continue;
    }
    if (FIXED_DATE_AIS.has(ai)) {
      fields[ai] = text.slice(index, index + 6);
      index += 6;
      continue;
    }

    const separator = text.indexOf(GS, index);
    if (separator === -1) {
      fields[ai] = text.slice(index);
      index = text.length;
    } else {
      fields[ai] = text.slice(index, separator);
      index = separator + 1;
    }
  }

  return recognized ? fieldsToResult(fields) : null;
}

export function barcodeLookupKeys(valueOrParsed) {
  const parsed = valueOrParsed && typeof valueOrParsed === 'object' && 'raw' in valueOrParsed
    ? valueOrParsed
    : parseInventoryBarcodePayload(valueOrParsed);
  if (!parsed?.raw) return [];

  const keys = [];
  if (parsed.gtin) keys.push(`gtin:${parsed.gtin}`);
  const plain = normalizePlain(parsed.raw);
  const plainGtin = normalizeGtin(plain);
  if (plainGtin) keys.push(`gtin:${plainGtin}`);
  if (plain) keys.push(`raw:${plain}`);
  return [...new Set(keys)];
}

export function parseInventoryBarcodePayload(value) {
  const raw = cleanRaw(value);
  if (!raw) return null;

  const gs1 = parseParenthesized(raw) || parseDigitalLink(raw) || parseRawGs1(raw);
  const result = {
    raw,
    isGs1: Boolean(gs1),
    gtin: gs1?.gtin || '',
    expirationDate: gs1?.expirationDate || '',
    lot: gs1?.lot || '',
    serial: gs1?.serial || '',
  };
  result.lookupKeys = barcodeLookupKeys(result);
  result.primaryLookupKey = result.lookupKeys[0] || '';
  return result;
}

export function gs1PrefillSummary(parsed) {
  if (!parsed?.isGs1) return '';
  const parts = [];
  if (parsed.lot) parts.push(`lot ${parsed.lot}`);
  if (parsed.expirationDate) parts.push(`expiration ${parsed.expirationDate}`);
  if (parsed.serial) parts.push(`serial ${parsed.serial}`);
  return parts.join(', ');
}
